import {
  Button,
  Checkbox,
  Divider,
  Form,
  InputNumber,
  Select,
  Slider,
} from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { FC, ReactNode, useEffect, useState } from "react";

type FilterFieldName =
  | "Prix"
  | "Usages"
  | "Date_Transaction"
  | "Pieces"
  | "Prix_m2_habitable"
  | "SurfaceBrutHab"
  | "Terrain";

type IncludeEmptyFieldName = Exclude<
  FilterFieldName,
  "Usages" | "Date_Transaction"
>;

const defaultIncludeEmpty: Record<IncludeEmptyFieldName, boolean> = {
  Prix: true,
  Pieces: true,
  Prix_m2_habitable: true,
  SurfaceBrutHab: true,
  Terrain: true,
};

const defaultFilterValues: any = {
  Prix: [0, 100_000_000],
  Usages: [],
  Date_Transaction: [2019, new Date().getFullYear()],
  Pieces: [],
  Prix_m2_habitable: [0, 48_000],
  SurfaceBrutHab: [0, 10_000],
  Terrain: [0, 10_000],
  includeEmpty: defaultIncludeEmpty,
};

const NULL_ONLY_EMPTY_FIELDS = new Set<FilterFieldName>([
  "Prix",
  "Date_Transaction",
  "Prix_m2_habitable",
  "SurfaceBrutHab",
  "Terrain",
  "Pieces",
]);

function shouldIncludeEmpty(
  includeEmpty: Record<IncludeEmptyFieldName, boolean>,
  field: IncludeEmptyFieldName
) {
  return includeEmpty[field] !== false;
}

function emptyValueClauses(field: FilterFieldName) {
  if (NULL_ONLY_EMPTY_FIELDS.has(field)) {
    return [{ [field]: { _null: true } }];
  }

  return [{ [field]: { _empty: true } }, { [field]: { _null: true } }];
}

function withEmptyValues(
  field: IncludeEmptyFieldName,
  clause: any,
  includeEmpty: Record<IncludeEmptyFieldName, boolean>
) {
  if (!shouldIncludeEmpty(includeEmpty, field)) {
    return clause;
  }

  return {
    _or: [clause, ...emptyValueClauses(field)],
  };
}

function excludeEmptyValues(field: IncludeEmptyFieldName) {
  if (NULL_ONLY_EMPTY_FIELDS.has(field)) {
    return { [field]: { _nnull: true } };
  }

  return { [field]: { _nempty: true } };
}

export function buildTransactionsFaoFilters(allValues: any) {
  const includeEmpty: Record<IncludeEmptyFieldName, boolean> = {
    ...defaultIncludeEmpty,
    ...allValues.includeEmpty,
  };
  const filters: any[] = [];

  filters.push(
    withEmptyValues(
      "Prix",
      { Prix: { _between: allValues.Prix } },
      includeEmpty
    )
  );

  if (allValues.Usages?.length > 0) {
    filters.push({
      _or: allValues.Usages.map((v: any) => ({
        Usages: { _contains: v },
      })),
    });
  }

  filters.push({
    Date_Transaction: {
      _between: [
        new Date(allValues.Date_Transaction[0], 0, 1).toISOString(),
        new Date(allValues.Date_Transaction[1], 12, 31).toISOString(),
      ],
    },
  });

  if (allValues.Pieces?.length > 0) {
    const filteredValues = allValues.Pieces.filter((v: any) => v !== "5+");
    const piecesClauses: any[] = [];

    if (filteredValues.length > 0) {
      piecesClauses.push({ Pieces: { _in: filteredValues } });
    }

    if (allValues.Pieces.includes("5+")) {
      piecesClauses.push({ Pieces: { _gte: "5" } });
    }

    if (piecesClauses.length > 0) {
      if (shouldIncludeEmpty(includeEmpty, "Pieces")) {
        piecesClauses.push(...emptyValueClauses("Pieces"));
      }
      filters.push({ _or: piecesClauses });
    }
  } else if (!shouldIncludeEmpty(includeEmpty, "Pieces")) {
    filters.push(excludeEmptyValues("Pieces"));
  }

  filters.push(
    withEmptyValues(
      "Prix_m2_habitable",
      { Prix_m2_habitable: { _between: allValues.Prix_m2_habitable } },
      includeEmpty
    )
  );

  filters.push(
    withEmptyValues(
      "SurfaceBrutHab",
      { SurfaceBrutHab: { _between: allValues.SurfaceBrutHab } },
      includeEmpty
    )
  );

  filters.push(
    withEmptyValues(
      "Terrain",
      { Terrain: { _between: allValues.Terrain } },
      includeEmpty
    )
  );

  return { _and: [...filters, { Type: { _in: ["Achat"] } }] };
}

export function getDefaultTransactionsFaoFilters() {
  return buildTransactionsFaoFilters(defaultFilterValues);
}

const TransactionsFaoFilters: FC<{
  onApplyFilters: (filters: any) => void;
}> = ({ onApplyFilters }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState(getDefaultTransactionsFaoFilters);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => handleFormValuesChange(defaultFilterValues), []);

  useEffect(() => {
    onApplyFilters(filters);
  }, [filters, onApplyFilters]);

  const handleFormValuesChange = (allValues: any) => {
    setFilters(buildTransactionsFaoFilters(allValues));
  };

  return (
    <Form
      form={form}
      initialValues={defaultFilterValues}
      style={{ padding: "16px 24px" }}
      layout="vertical"
      size="small"
      onValuesChange={(_, allValues) => handleFormValuesChange(allValues)}
    >
      <div
        style={{
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          padding: "8px 0",
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
          <span>Filtres transactions_fao</span>
        </div>
        {!isCollapsed && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              form.resetFields();
              handleFormValuesChange(defaultFilterValues);
            }}
          >
            Réinitialiser
          </Button>
        )}
      </div>
      {!isCollapsed && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: -8,
            }}
          >
            <Form.Item label="Prix de vente - CHF" name={["Prix", 0]}>
              <InputNumber step={1000} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["Prix", 1]}>
              <InputNumber step={1000} type="number" />
            </Form.Item>
          </div>
          <IncludeEmptyCheckbox field="Prix" />

          <FilterField label="Type de bien" name="Usages">
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              options={[
                { label: "Maison", value: "Habitation à un seul logement" },
                { label: "Immeuble", value: "Habitation à plusieurs logements" },
                { label: "Hôtel", value: "Hôtel" },
                { label: "Appartement", value: "Appartement" },
                { label: "Terrain", value: "Terrain" },
                { label: "Garage", value: "Garage" },
                { label: "Parking", value: "Parking" },
                { label: "Bureau", value: "Bureau" },
                { label: "Local commercial", value: "Local commercial"},
                { label: "Commerce", value: "Commerce"}
              ]}
            />
          </FilterField>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Form.Item label="Date de vente" name={["Date_Transaction", 0]}>
              <InputNumber step={1} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["Date_Transaction", 1]}>
              <InputNumber step={1} type="number" />
            </Form.Item>
          </div>
{/*
          <FilterField label="Nombre de pièces" name="Pieces">
            <Checkbox.Group style={{ width: "100%" }}>
              <Checkbox value="1">1</Checkbox>
              <Checkbox value="2">2</Checkbox>
              <Checkbox value="3">3</Checkbox>
              <Checkbox value="4">4</Checkbox>
              <Checkbox value="5+">5+</Checkbox>
            </Checkbox.Group>
          </FilterField>
*/}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: -8,
            }}
          >
            <Form.Item label="Prix au m2 - CHF" name={["Prix_m2_habitable", 0]}>
              <InputNumber step={1000} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["Prix_m2_habitable", 1]}>
              <InputNumber step={1000} type="number" />
            </Form.Item>
          </div>
          <IncludeEmptyCheckbox field="Prix_m2_habitable" />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: -8,
            }}
          >
            <Form.Item label="Surface - m2" name={["SurfaceBrutHab", 0]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["SurfaceBrutHab", 1]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
          </div>
          <IncludeEmptyCheckbox field="SurfaceBrutHab" />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: -8,
            }}
          >
            <Form.Item label="Terrain - m2" name={["Terrain", 0]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["Terrain", 1]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
          </div>
          <IncludeEmptyCheckbox field="Terrain" />
        </>
      )}
    </Form>
  );
};

export default TransactionsFaoFilters;

const FilterField: FC<{
  label: string;
  name: string;
  children: ReactNode;
  className?: string;
}> = ({ label, name, children, className = "" }) => {
  return (
    <Form.Item label={label} name={name} className={className}>
      {children}
    </Form.Item>
  );
};

const IncludeEmptyCheckbox: FC<{ field: IncludeEmptyFieldName }> = ({
  field,
}) => (
  <Form.Item
    name={["includeEmpty", field]}
    valuePropName="checked"
    style={{
      marginTop: -16,
      marginBottom: 10,
      transform: "scale(0.8)",
      transformOrigin: "left top",
    }}
  >
    <Checkbox style={{ color: "rgba(0, 0, 0, 0.55)" }}>
      Inclure les valeurs vides
    </Checkbox>
  </Form.Item>
);
